import { inject, Injectable, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PlanTemplateDto, RentalDto, Wallet } from '../../api/models/fraud.models';
import UpdateWalletComponent from '../../pages/update-wallet/update-wallet.component';
import { DialogBalanceComponent } from '../components/dialogs/dialog-balance/dialog-balance.component';
import { DialogBenefitBusinessAccountComponent } from '../components/dialogs/dialog-benefit-business-account/dialog-benefit-business-account.component';
import { DialogCancelInvoiceComponent } from '../components/dialogs/dialog-cancel-invoice/dialog-cancel-invoice.component';
import { DialogDesktopBenefitsComponent } from '../components/dialogs/dialog-desktop-benefits/dialog-desktop-benefits.component';
import { DialogAccessDeniedComponent } from '../components/dialogs/dialog-access-denied/dialog-access-denied.component';
import { DialogAdvancedAmlProtectionTriggersComponent } from '../components/dialogs/dialog-advanced-aml-protection-triggers/dialog-advanced-aml-protection-triggers.component';
import { DialogFirstActivateStartTrialComponent } from '../components/dialogs/dialog-first-activate-start-trial/dialog-first-activate-start-trial.component';
import { DialogInvoicePlanUpgradeComponent } from '../components/dialogs/dialog-invoice-plan-upgrade/dialog-invoice-plan-upgrade.component';
import { DialogNavMenu } from '../components/dialogs/dialog-nav-menu/dialog-nav-menu';
import { DialogPurchaseFlowComponent } from '../components/dialogs/dialog-purchase-flow/dialog-purchase-flow.component';
import { DialogSubscriptionExpiredComponent } from '../components/dialogs/dialog-subscription-expired/dialog-subscription-expired.component';
import { DialogSwitchAccountComponent } from '../components/dialogs/dialog-switch-account/dialog-switch-account.component';
import { TalkToSalesData, DialogTalkToSalesComponent } from '../components/dialogs/dialog-talk-to-sales/dialog-talk-to-sales.component';
import { DialogVerifyAmlReportComponent } from '../components/dialogs/dialog-verify-general-aml-report/dialog-verify-aml-report.component';
import {
  DialogEndSessionComponent,
  EndSessionDialogData,
} from '../components/dialogs/dialog-end-session/dialog-end-session.component';
import {
  BonusSurveyDialogData,
  DialogBonusSurveyComponent,
} from '../components/dialogs/dialog-bonus-survey/dialog-bonus-survey.component';
import {
  WelcomeBonusDialogData,
  DialogWelcomeBonusComponent,
} from '../components/dialogs/dialog-welcome-bonus/dialog-welcome-bonus.component';
import {
  RenewalData,
  RenewalDialogClosePayload,
  DialogRenewalComponent,
} from '../components/dialogs/dialog-renewal/dialog-renewal.component';

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private readonly _matDialog: MatDialog = inject(MatDialog);

  openSwitchAccountDialog(): MatDialogRef<DialogSwitchAccountComponent, unknown> {
    return this._matDialog.open(DialogSwitchAccountComponent, {
      width: '92%',
      maxWidth: '100%',
      maxHeight: '98%',
      height: 'auto',
      panelClass: 'dialog-switch-account',
      backdropClass: 'backdrop-green',
      position: { top: '10px' },
      restoreFocus: false,
    });
  }

  openNavMenuDialog(): MatDialogRef<DialogNavMenu, unknown> {
    return this._matDialog.open(DialogNavMenu, {
      width: '100%',
      maxWidth: '100%',
      maxHeight: '98%',
      height: '100%',
      panelClass: 'dialog-check-wallet-menu',
      backdropClass: 'backdrop-litegreen',
      position: { top: '10px' },
      restoreFocus: false,
    });
  }

  openBalanceDialog(options?: { restoreFocus?: boolean }): MatDialogRef<DialogBalanceComponent, unknown> {
    return this._matDialog.open(DialogBalanceComponent, {
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      panelClass: 'dialog-balance',
      restoreFocus: options?.restoreFocus,
    });
  }

  openDesktopBenefitsDialog(): MatDialogRef<DialogDesktopBenefitsComponent, unknown> {
    return this._matDialog.open(DialogDesktopBenefitsComponent, {
      width: '92%',
      maxWidth: '100%',
      maxHeight: '98%',
      height: 'auto',
      panelClass: 'dialog-switch-account',
      backdropClass: 'backdrop-green',
      position: { top: '10px' },
      restoreFocus: false,
    });
  }

  openBenefitBusinessAccountDialog(): MatDialogRef<DialogBenefitBusinessAccountComponent, unknown> {
    return this._matDialog.open(DialogBenefitBusinessAccountComponent, {
      width: '92%',
      maxWidth: '100%',
      maxHeight: '98%',
      height: 'auto',
      panelClass: 'dialog-switch-account',
      backdropClass: 'backdrop-green',
      position: { top: '10px' },
      restoreFocus: false,
    });
  }

  openAccessDeniedDialog(): MatDialogRef<DialogAccessDeniedComponent, unknown> {
    return this._matDialog.open(DialogAccessDeniedComponent, {
      backdropClass: 'activate-package-overlay',
      height: 'auto',
      panelClass: 'dialog-add-wallet',
    });
  }

  openTalkToSalesDialog(data: TalkToSalesData): MatDialogRef<DialogTalkToSalesComponent, unknown> {
    return this._matDialog.open(DialogTalkToSalesComponent, {
      panelClass: 'talk-to-sales-dialog-panel',
      backdropClass: 'talk-to-sales-dialog-backdrop',
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      autoFocus: false,
      data,
    });
  }

  openPurchaseFlowDialog(
    planTemplate: PlanTemplateDto,
  ): MatDialogRef<DialogPurchaseFlowComponent, RenewalDialogClosePayload> {
    return this._matDialog.open(DialogPurchaseFlowComponent, {
      panelClass: 'purchase-flow-dialog-panel',
      backdropClass: 'purchase-flow-dialog-backdrop',
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      autoFocus: false,
      data: planTemplate,
    });
  }

  openInvoicePlanUpgradeDialog(invoiceUuid: string): MatDialogRef<DialogInvoicePlanUpgradeComponent, unknown> {
    return this._matDialog.open(DialogInvoicePlanUpgradeComponent, {
      panelClass: 'purchase-flow-dialog-panel',
      backdropClass: 'purchase-flow-dialog-backdrop',
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      autoFocus: false,
      data: invoiceUuid,
    });
  }

  openRenewalDialog(
    data?: RenewalData,
  ): MatDialogRef<DialogRenewalComponent, RenewalDialogClosePayload> {
    return this._matDialog.open(DialogRenewalComponent, {
      panelClass: 'renewal-dialog-panel',
      backdropClass: 'stage-dialog-backdrop',
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      autoFocus: false,
      data,
    });
  }

  openSubscriptionExpiredDialog(id: string): MatDialogRef<DialogSubscriptionExpiredComponent, unknown> {
    return this._matDialog.open(DialogSubscriptionExpiredComponent, {
      id,
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      panelClass: 'dialog-subscription-expired',
      hasBackdrop: true,
    });
  }

  openFirstActivateStartTrialDialog(): MatDialogRef<DialogFirstActivateStartTrialComponent, unknown> {
    return this._matDialog.open(DialogFirstActivateStartTrialComponent, {
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      panelClass: 'dialog-first-activate-start-trial',
    });
  }

  openAdvancedAmlProtectionTriggersDialog(): MatDialogRef<DialogAdvancedAmlProtectionTriggersComponent, unknown> {
    return this._matDialog.open(DialogAdvancedAmlProtectionTriggersComponent, {
      width: '335px',
      panelClass: 'dialog-advanced-aml-protection-triggers',
      backdropClass: 'bg-transparent',
      restoreFocus: false,
    });
  }

  openUpdateWalletDialog(wallet: Wallet): MatDialogRef<UpdateWalletComponent, unknown> {
    return this._matDialog.open(UpdateWalletComponent, {
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      panelClass: 'dialog-update-wallet',
      data: { wallet },
    });
  }

  openCancelInvoiceDialog(data: {
    walletHeaderTemplate: TemplateRef<unknown>;
    rental: RentalDto;
  }): MatDialogRef<DialogCancelInvoiceComponent, unknown> {
    return this._matDialog.open(DialogCancelInvoiceComponent, {
      maxWidth: '600px',
      width: '600px',
      height: '328px',
      panelClass: 'mat-dialog-bottom-notification',
      position: { bottom: '0px' },
      data,
      restoreFocus: false,
    });
  }

  openVerifyAmlReportDialog(wallet: Wallet): MatDialogRef<DialogVerifyAmlReportComponent, unknown> {
    return this._matDialog.open(DialogVerifyAmlReportComponent, {
      width: '100%',
      maxWidth: '344px',
      height: 'auto',
      panelClass: 'dialog-verify-report',
      backdropClass: 'backdrop-green',
      data: wallet,
      restoreFocus: false,
    });
  }
  

  openBonusSurveyDialog(data: BonusSurveyDialogData): MatDialogRef<DialogBonusSurveyComponent, unknown> {
    return this._matDialog.open(DialogBonusSurveyComponent, {
      panelClass: 'bonus-survey-dialog-panel',
      backdropClass: 'bonus-survey-dialog-backdrop',
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      autoFocus: 'dialog',
      data,
    });
  }

  openWelcomeBonusDialog(data: WelcomeBonusDialogData): MatDialogRef<DialogWelcomeBonusComponent, boolean> {
    return this._matDialog.open(DialogWelcomeBonusComponent, {
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      panelClass: 'welcome-bonus-dialog-panel',
      autoFocus: 'dialog',
      data,
    });
  }

  openEndSessionDialog(data: EndSessionDialogData): MatDialogRef<DialogEndSessionComponent, boolean> {
    return this._matDialog.open(DialogEndSessionComponent, {
      width: '100%',
      maxWidth: '343px',
      height: 'auto',
      panelClass: 'dialog-end-session',
      backdropClass: 'backdrop-green',
      restoreFocus: false,
      data,
    });
  }

  getDialogById<TComponent = unknown, TResult = unknown>(id: string): MatDialogRef<TComponent, TResult> | undefined {
    return this._matDialog.getDialogById(id) as MatDialogRef<TComponent, TResult> | undefined;
  }
}
